import { WorkflowInputType, DocumentType } from './constants';
import dayjs from 'dayjs';
import isYesterday from 'dayjs/plugin/isYesterday';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

export const findByValue = (obj, val) => {
  return Object.values(obj).find((item) => item.value === val);
};

export const isFileInput = (type) => type === WorkflowInputType.FILE.value;

export const formatNumber = (num) => {
  if (num == null) return '-';
  return new Intl.NumberFormat('en-IN').format(num);
};

export const truncateFileName = (name, maxLength = 20) => {
  if (!name) return '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return name;

  const base = name.substring(0, dotIndex);
  const ext = name.substring(dotIndex);

  if (base.length > maxLength) {
    return base.substring(0, maxLength) + '...' + ext;
  }
  return name;
};
export const prettyBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const formatETA = (seconds) => {
  if (!isFinite(seconds) || seconds < 0) return '~calculating…';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) return `~${h}h ${m}m left`;
  if (m > 0) return `~${m}m ${s}s left`;
  return `~${s}s left`;
};

export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const createBottomLegend = (data, spacing = 20, bottom = 10) => {
  const elements = [];
  let currentLeft = 0;

  data.forEach((item) => {
    // Approximate text width (font size 12 → roughly 7px per character)
    const textWidth = item.name.length * 7;

    // Circle
    elements.push({
      type: 'circle',
      shape: { r: 6 },
      style: { fill: item.itemStyle?.color || '#ccc' },
      left: currentLeft,
      top: 0,
    });

    // Text
    elements.push({
      type: 'text',
      left: currentLeft + 16, // add circle diameter + padding
      top: 1,
      style: { text: item.name, fontSize: 12, fill: '#333' },
    });

    // Increment currentLeft for the next pair
    currentLeft += 14 + textWidth + spacing;
  });

  return [
    {
      type: 'group',
      left: 'center',
      bottom,
      children: elements,
    },
  ];
};

export const getDocumentLabels = (docObject) => {
  const tempArr = [];
  for (const key of Object.keys(docObject)) {
    const label = docObject[key]?.label;
    const value = docObject[key]?.value;
    tempArr.push({ value, label });
  }
  return tempArr;
};

export const getDocumentValues = (label) => {
  const capitalCase = label?.split(' ')?.join('_')?.toUpperCase();
  return DocumentType[capitalCase]?.value;
};

export const capitalizeWords = (str = '') =>
  str
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const typeText = async (newText, fullTextRef, assistantMsgId, setMessages) => {
  for (let i = 0; i < newText.length; i++) {
    // Append next character
    fullTextRef.current += newText[i];
    setMessages((prev) =>
      prev.map((m) => (m.id === assistantMsgId ? { ...m, message: fullTextRef.current } : m))
    );

    // Random delay between keystrokes (simulate human typing)
    let delay = Math.floor(Math.random() * 20) + 5; // 5–25ms typical
    await new Promise((r) => setTimeout(r, delay));
  }
};

// Simulate human-like random typing pace
export const appendChunk = async ({
  chunkText,
  fullTextRef,
  assistantMsgId,
  setMessages,
  isFinalizingRef,
}) => {
  // if (isFinalizingRef?.current) {
  //   // Final mode: append fast, no delay
  //   fullTextRef.current += chunkText;
  //   setMessages((prev) =>
  //     prev.map((m) => (m.id === assistantMsgId ? { ...m, text: fullTextRef.current } : m))
  //   );
  //   return;
  // }

  // Decide whether to type slowly or fast
  // const randomMode = Math.random();
  // if (randomMode < 0.2) {
  //   // 20% of the time, use typing effect
  //   await typeText(chunkText, fullTextRef, assistantMsgId, setMessages);
  // } else {
  // 80% of the time, append chunk instantly (fast burst)
  fullTextRef.current += chunkText;
  setMessages((prev) =>
    prev.map((m) => (m.id === assistantMsgId ? { ...m, message: fullTextRef.current } : m))
  );

  // Random pause before next burst
  const randomDelay = Math.floor(Math.random() * (10 - 5 + 1)) + 5; // 5–10ms

  const shouldBatch = Math.random() < 0.8;

  await new Promise((res) => setTimeout(res, shouldBatch ? 0 : randomDelay));
  // }
};

export const getDateCategory = (date) => {
  dayjs.extend(isYesterday);
  dayjs.extend(isSameOrAfter);

  const d = dayjs(date);
  const now = dayjs();

  if (d.isSame(now, 'day')) return 'Today';
  if (d.isYesterday()) return 'Yesterday';

  // Last 7 days (excluding today & yesterday)
  if (d.isSameOrAfter(now.subtract(7, 'days'), 'day')) return 'Last Week';

  // Last 30 days (excluding above)
  if (d.isSameOrAfter(now.subtract(30, 'days'), 'day')) return 'Last Month';

  return 'Older';
};

export const formatToMillionBillion = (value, useCommaFormat = false) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  const num = Number(value);

  if (num >= 1_000_000_000) {
    return `$${(num / 1_000_000_000).toFixed(2)}B`;
  }

  if (num >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(2)}M`;
  }

  return useCommaFormat
    ? `$${num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : `$${num.toFixed(2).toLocaleString()}`;
};

/**
 * Converts seconds into formatted time
 *
 * @param {number} totalSeconds
 * @param {Object} options
 * @param {boolean} options.showSeconds - HH:mm:ss vs HH:mm
 * @param {boolean} options.humanize - show like "3.2mins"
 *
 * @returns {string}
 */
export const formatSeconds = (
  totalSeconds,
  { showSeconds = true, humanReadable = true, decimalPlaces = 1 } = {}
) => {
  if (humanReadable) {
    const DAY = 86400;
    const HOUR = 3600;
    const MIN = 60;

    if (totalSeconds < MIN) {
      return `${totalSeconds}s`;
    }

    if (totalSeconds < HOUR) {
      return `${(totalSeconds / MIN).toFixed(decimalPlaces)} mins`;
    }

    if (totalSeconds < DAY) {
      return `${(totalSeconds / HOUR).toFixed(decimalPlaces)} hrs`;
    }

    return `${(totalSeconds / DAY).toFixed(decimalPlaces)} days`;
  }

  const DAY = 86400;

  const days = Math.floor(totalSeconds / DAY);
  const remainderAfterDays = totalSeconds % DAY;

  const hours = Math.floor(remainderAfterDays / 3600);
  const minutes = Math.floor((remainderAfterDays % 3600) / 60);
  const seconds = Math.floor(remainderAfterDays % 60);

  const time = showSeconds
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  return days > 0 ? `${days}d ${time}` : time;
};
